import { h } from "../../core/dom.js";
import { copy } from "../../config/copy.js";
import { CameraController } from "../../camera/cameraController.js";
import { LiveAnalyzer } from "../../video/liveAnalyzer.js";
import { analyzeVideoFile } from "../../video/frameSampler.js";
import { AnalysisUnavailableError } from "../../inference/hybridAnalyzer.js";
import { attemptsFor, findDog } from "../../inference/dogSearch.js";
import { framingSnapshot, snapshotJpeg } from "../../inference/preprocess.js";
import { makeThumbnail } from "../../storage/thumbnail.js";
import { probabilityBars } from "../components/probabilityBars.js";
import { createBoxOverlay } from "../components/boxOverlay.js";

const CAMERA_MESSAGES = { denied: copy.cameraDenied, missing: copy.cameraMissing, error: copy.cameraError };
const CLIP_SNAPSHOTS = [0.2, 0.5, 0.8];

export function analyzeView(app) {
  const video = h("video", { class: "preview", playsinline: true, muted: true, autoplay: true });
  const guideLabel = h("span", {}, copy.tapToAnalyze);
  const guide = h("div", { class: "guide" }, guideLabel);
  const liveBadge = h("div", { class: "live-badge hidden" });
  const overlay = h("canvas", { class: "overlay" });
  const coachLine = h("div", { class: "coach-line hidden" });
  const livePanel = h("div", { class: "live-panel hidden" });
  const busy = h("div", { class: "busy hidden" }, h("span", { class: "spinner" }), h("span", {}, "Analizzo…"));
  const stage = h("div", {
    class: "stage stage-tap",
    role: "button",
    tabindex: "0",
    "aria-label": "Analizza l'inquadratura"
  }, video, overlay, guide, liveBadge, coachLine, busy);
  const placeholder = h("div", { class: "stage-placeholder" },
    h("div", { class: "hero-emoji" }, "🐶"),
    h("p", {}, copy.tagline),
    h("button", { class: "btn btn-primary", onclick: () => startCamera() }, "📷 Apri la fotocamera"));
  const fileInput = h("input", { type: "file", accept: "image/*,video/*", class: "hidden" });
  const camera = new CameraController(video);
  const boxes = createBoxOverlay(overlay, video);
  const reportDetector = (error) => app.reporter.report(error, "detector");
  let coachTimer = null;
  const showCoach = (text) => {
    coachLine.textContent = text;
    coachLine.classList.remove("hidden");
    clearTimeout(coachTimer);
    coachTimer = setTimeout(() => coachLine.classList.add("hidden"), 12000);
  };
  const hideCoach = () => {
    clearTimeout(coachTimer);
    coachLine.classList.add("hidden");
    coachLine.textContent = "";
  };
  const coachFrame = (source) => snapshotJpeg(source, 512, 0.7);
  const quickResult = (distributions) => {
    if (!distributions?.length || !app.classifier.ready) return null;
    try {
      return app.classifier.describe(distributions[0]);
    } catch (error) {
      app.reporter.report(error, "coach-result");
      return null;
    }
  };
  const observe = (phase, source, result = null, force = true) => {
    if (!app.coach.enabled) return;
    app.coach.observe({ phase, imageDataUrl: coachFrame(source), result, force })
      .then((text) => { if (text) showCoach(text); });
  };
  let releaseCamera = null;
  let live = null;
  let clipTimers = [];
  let working = false;

  const notReadyMessage = () => {
    const status = app.store.state.modelStatus;
    if (status === "missing") return copy.modelMissing;
    if (status === "error") return copy.modelError;
    return copy.modelLoading;
  };

  const ensureReady = () => {
    if (app.analyzer.ready) return true;
    app.toast(notReadyMessage(), "warning");
    return false;
  };

  const setBusy = (value) => {
    working = value;
    busy.classList.toggle("hidden", !value);
    stage.classList.toggle("stage-busy", value);
  };

  const finish = async ({ distributions, snapshots, source, imageDataUrl, thumbnail }) => {
    setBusy(true);
    try {
      const { result, engine, modelVersion } = await app.analyzer.analyze({ distributions, snapshots, source });
      let historyId = null;
      try {
        const item = await app.history.add({
          label: result.top.id, label_it: result.top.label_it, emoji: result.top.emoji, confidence: result.confidence, uncertain: result.uncertain, source, engine,
          thumbnail, probabilities: result.probabilities
        });
        historyId = item.id;
      } catch (error) {
        app.reporter.report(error, "history");
      }
      app.sheet.open({ result, imageDataUrl, source, historyId, engine, modelVersion });
    } catch (error) {
      if (error instanceof AnalysisUnavailableError) app.toast(navigator.onLine ? notReadyMessage() : copy.offlineNoModel, "warning");
      else {
        app.reporter.report(error, `analyze-${source}`);
        app.toast(copy.modelError, "error");
      }
    } finally {
      setBusy(false);
    }
  };

  const closeCamera = (fromBack = false) => {
    stopLive();
    hideCoach();
    camera.stop();
    stage.classList.add("hidden");
    controls.classList.add("hidden");
    placeholder.classList.remove("hidden");
    if (!fromBack && releaseCamera) releaseCamera();
    releaseCamera = null;
  };

  async function startCamera() {
    try {
      await camera.start();
      placeholder.classList.add("hidden");
      stage.classList.remove("hidden");
      controls.classList.remove("hidden");
      releaseCamera = app.back?.push(() => closeCamera(true));
    } catch (error) {
      app.toast(CAMERA_MESSAGES[error.kind] || copy.cameraError, "error");
      if (error.kind === "error") app.reporter.report(error.cause || error, "camera");
    }
  }

  const dogInFrame = async (source) => {
    const detection = await findDog(app.detector, source, { attempts: attemptsFor(source), onError: reportDetector });
    if (app.devMode.enabled && detection) app.toast(copy.devDetector(detection.best, detection.dogs.length), "info", 4000);
    if (detection?.empty) {
      app.toast(copy.noDog, "warning", 2200);
      return false;
    }
    return true;
  };

  const takePhoto = async () => {
    if (working || !ensureReady() || !camera.active) return;
    try {
      if (!(await dogInFrame(video))) return;
      const distributions = app.classifier.ready ? [await app.classifier.probabilities(video, "center")] : [];
      observe("photo", video, quickResult(distributions));
      const snapshot = framingSnapshot(video, "center");
      await finish({ distributions, snapshots: [snapshot], source: "photo", imageDataUrl: snapshot, thumbnail: makeThumbnail(video) });
    } catch (error) {
      app.reporter.report(error, "photo");
      app.toast(copy.modelError, "error");
    }
  };

  const stopLive = () => {
    live?.stop();
    clipTimers.forEach(clearTimeout);
    clipTimers = [];
    liveBadge.classList.add("hidden");
    livePanel.classList.add("hidden");
    guideLabel.textContent = copy.tapToAnalyze;
    boxes.clear();
    liveButton.textContent = "🎥 Live";
    clipButton.disabled = false;
  };

  const startLive = () => {
    if (!camera.active) return;
    if (!app.classifier.ready) return app.toast(copy.liveNeedsModel, "warning");
    live = new LiveAnalyzer(app.classifier, video, {
      intervalMs: app.config.liveIntervalMs,
      detector: app.detector,
      onUpdate: (result, frames, detection) => {
        const name = result.uncertain ? `Forse ${result.top.label_it.toLowerCase()}` : result.top.label_it;
        liveBadge.textContent = `${result.top.emoji} ${name} · ${Math.round(result.confidence * 100)}%`;
        livePanel.replaceChildren(probabilityBars(result.ranking, 3), h("p", { class: "fineprint" }, `${frames} fotogrammi osservati`));
        if (showBoxes) boxes.draw(detection?.dogs, detection?.region);
        if (app.coachLive.enabled && app.coach.due()) observe("live", video, result, false);
      },
      onEmpty: () => {
        liveBadge.textContent = `🐾 ${copy.liveNoDog}`;
        livePanel.replaceChildren(h("p", { class: "fineprint" }, copy.noDog));
        boxes.clear();
      },
      onError: (error) => { app.reporter.report(error, "live"); app.toast(copy.modelError, "error"); stopLive(); }
    });
    live.start();
    guideLabel.textContent = copy.tapLiveHint;
    liveBadge.textContent = "Osservo…";
    liveBadge.classList.remove("hidden");
    livePanel.classList.remove("hidden");
    liveButton.textContent = "⏹ Stop";
  };

  const startClip = async () => {
    if (working || !ensureReady() || !camera.active) return;
    if (!(await dogInFrame(video))) return;
    observe("clip", video);
    const seconds = app.config.clipSeconds;
    const snapshots = [];
    if (app.classifier.ready) startLive();
    liveBadge.textContent = `⏺ Osservo per ${seconds} secondi…`;
    liveBadge.classList.remove("hidden");
    clipButton.disabled = true;
    liveButton.textContent = "⏹ Stop";
    clipTimers = CLIP_SNAPSHOTS.map((ratio) => setTimeout(() => snapshots.push(framingSnapshot(video, "center")), ratio * seconds * 1000));
    clipTimers.push(setTimeout(async () => {
      const distributions = live?.samples ? [...live.samples] : [];
      const thumbnail = makeThumbnail(video);
      stopLive();
      if (!distributions.length && !snapshots.length) return app.toast(copy.uncertain, "warning");
      await finish({ distributions, snapshots, source: "clip", imageDataUrl: snapshots[1] || snapshots[0], thumbnail });
    }, seconds * 1000));
  };

  const onFile = async (file) => {
    if (!file || working || !ensureReady()) return;
    try {
      if (file.type.startsWith("video/")) {
        setBusy(true);
        const { distributions, snapshots, thumbnail, dogFrames, checkedFrames } = await analyzeVideoFile(file, app.classifier,
          { frames: 12, snapshots: 4, detector: app.detector, onError: reportDetector });
        if (checkedFrames && !dogFrames) {
          setBusy(false);
          return app.toast(copy.noDog, "warning", 2200);
        }
        await finish({ distributions, snapshots, source: "video", imageDataUrl: snapshots[0], thumbnail });
      } else {
        const image = await createImageBitmap(file, { imageOrientation: "from-image" });
        if (!(await dogInFrame(image))) return;
        const distributions = app.classifier.ready ? [await app.classifier.probabilities(image, "fit")] : [];
        observe("upload", image, quickResult(distributions));
        const snapshot = snapshotJpeg(image, 640, 0.85);
        await finish({ distributions, snapshots: [snapshot], source: "upload", imageDataUrl: snapshot, thumbnail: makeThumbnail(image) });
      }
    } catch (error) {
      app.reporter.report(error, "upload");
      app.toast(file.type.startsWith("video/") ? copy.videoError : copy.imageError, "error");
      setBusy(false);
    } finally {
      fileInput.value = "";
    }
  };
  fileInput.addEventListener("change", () => onFile(fileInput.files?.[0]));

  let showBoxes = app.boxes.enabled;
  const boxesButton = h("button", { class: "btn btn-icon", "aria-label": "Mostra o nascondi il riquadro sul cane" }, showBoxes ? "🔲" : "⬛");
  boxesButton.addEventListener("click", () => {
    showBoxes = app.boxes.toggle();
    boxesButton.textContent = showBoxes ? "🔲" : "⬛";
    if (!showBoxes) boxes.clear();
    app.toast(showBoxes ? copy.boxesOn : copy.boxesOff, "info", 2500);
  });

  const liveButton = h("button", { class: "btn btn-secondary", onclick: () => (live?.running || clipTimers.length ? stopLive() : startLive()) }, "🎥 Live");
  const clipButton = h("button", { class: "btn btn-secondary", onclick: startClip }, `⏺ Clip ${app.config.clipSeconds}s`);
  const flipButton = h("button", { class: "btn btn-icon", "aria-label": "Passa all'altra fotocamera" }, "🤳");
  flipButton.addEventListener("click", () => camera.flip().catch((error) =>
    app.toast(CAMERA_MESSAGES[error.kind] || copy.cameraError, "error")));

  const onStageTap = async () => {
    if (!camera.active || working) return;
    if (live?.running || clipTimers.length) stopLive();
    await takePhoto();
  };
  stage.addEventListener("click", onStageTap);
  stage.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onStageTap();
  });

  const controls = h("div", { class: "controls hidden" },
    flipButton,
    liveButton,
    clipButton,
    app.detector?.configured ? boxesButton : null);

  stage.classList.add("hidden");
  const element = h("section", { class: "view view-analyze" },
    placeholder, stage, livePanel, controls,
    h("div", { class: "upload-row" },
      h("button", { class: "btn btn-ghost wide", onclick: () => fileInput.click() }, "🖼️ Carica foto o video dalla galleria"),
      fileInput),
    h("p", { class: "fineprint center" }, "Consiglio: muso e corpo ben visibili, buona luce, un solo cane."));

  return {
    element,
    destroy() {
      closeCamera();
    }
  };
}
